import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Snackbar,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCookie } from "../../utils/cookies";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import stekayLogoBlue from "../../assets/logo-blue.png";
import pmsGoLogo from "../../assets/pmsgo-logo.png";
import loginShowcaseImg from "../../assets/login-showcase.png";
import { getBrandConfig } from "../../utils/brandConfig";

import { ALL_ROUTES } from "../../routes/RoleRoute";

const normalizeRole = (role) => {
  const roleName = typeof role === "object" && role !== null ? role.name : role;
  return String(roleName || "").replace(/[_\s-]+/g, "").toUpperCase();
};

const getInitialRoute = (user, tenantList = []) => {
  const isSuperAdmin = normalizeRole(user?.role) === "SUPERADMIN";
  if (isSuperAdmin) return "/users";
  if (tenantList.length >= 2) return "/select-tenant";

  let perms = [];
  if (Array.isArray(user?.customPermissions) && user.customPermissions.length > 0) {
    perms = user.customPermissions;
  } else if (Array.isArray(user?.permissions) && user.permissions.length > 0) {
    perms = user.permissions;
  } else if (user?.role && Array.isArray(user.role.permissions)) {
    perms = user.role.permissions;
  }
  const effectivePermissions = perms
    .map((p) => (typeof p === "string" ? p : p?.key || p?.name || p?.code || ""))
    .filter(Boolean);

  if (effectivePermissions.includes("*") || effectivePermissions.includes("CAN_VIEW_DASHBOARD")) {
    return "/dashboard";
  }

  const firstAllowed = ALL_ROUTES.find(
    (route) =>
      !route.permissions ||
      route.permissions.length === 0 ||
      route.permissions.some((perm) => effectivePermissions.includes(perm))
  );

  return firstAllowed ? firstAllowed.path : "/dashboard";
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error, accountLocked } = useSelector((state) => state.auth);
  const brandConfig = getBrandConfig();
  const branding = useSelector((state) => state.branding) || brandConfig;
  const brand = branding;

  useEffect(() => {
    dispatch.branding.loadPublicBrandingAsync();
  }, [dispatch]);

  const [terminatedMsg, setTerminatedMsg] = useState(() => {
    const msg = sessionStorage.getItem("sessionTerminatedMessage");
    if (msg) {
      sessionStorage.removeItem("sessionTerminatedMessage");
    }
    return msg || "";
  });

  // Dynamically update document title and metadata for the login portal
  useEffect(() => {
    const title = brand.portalTitle;
    const desc = brand.portalDesc;
    const currentUrl = window.location.href.includes("http")
      ? window.location.href
      : "https://pmsdemo.hotelss.in/login";

    document.title = title;

    const updateMetaTag = (selector, attr, val) => {
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute(attr, val);
      } else {
        const isLink = selector.startsWith("link");
        el = document.createElement(isLink ? "link" : "meta");
        if (selector.includes("name=")) {
          const name = selector.match(/name="([^"]+)"/)?.[1];
          if (name) el.setAttribute("name", name);
        } else if (selector.includes("property=")) {
          const prop = selector.match(/property="([^"]+)"/)?.[1];
          if (prop) el.setAttribute("property", prop);
        } else if (selector.includes("rel=")) {
          const rel = selector.match(/rel="([^"]+)"/)?.[1];
          if (rel) el.setAttribute("rel", rel);
        }
        el.setAttribute(attr, val);
        document.head.appendChild(el);
      }
    };

    updateMetaTag('meta[name="description"]', "content", desc);
    updateMetaTag('meta[name="title"]', "content", title);
    updateMetaTag('link[rel="canonical"]', "href", currentUrl);
    updateMetaTag('meta[property="og:title"]', "content", title);
    updateMetaTag('meta[property="og:description"]', "content", desc);
    updateMetaTag('meta[property="og:url"]', "content", currentUrl);
    updateMetaTag('meta[name="twitter:title"]', "content", title);
    updateMetaTag('meta[name="twitter:description"]', "content", desc);
    updateMetaTag('meta[name="twitter:url"]', "content", currentUrl);
  }, []);

  // Clear any leftover error when mounting or unmounting the login component
  useEffect(() => {
    dispatch.auth.setError(null);
    return () => {
      dispatch.auth.setError(null);
    };
  }, [dispatch]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) {
      dispatch.auth.setError(null);
    }
    if (terminatedMsg) {
      setTerminatedMsg("");
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) {
      dispatch.auth.setError(null);
    }
    if (terminatedMsg) {
      setTerminatedMsg("");
    }
  };

  const handleMfaCodeChange = (e) => {
    setMfaCode(e.target.value);
    if (error) {
      dispatch.auth.setError(null);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // Reset account lock state on new login attempt
    if (accountLocked) {
      dispatch.auth.setAccountLocked(false);
    }

    if (mfaStep) {
      const result = await dispatch.auth.verifyMfaAsync({ tempToken, mfaCode, rememberDevice });
      if (result?.success) {
        const user = JSON.parse(getCookie("adminAuth") || "{}");
        const isSuperAdmin = normalizeRole(user.role) === "SUPERADMIN";

        if (isSuperAdmin) {
          setShowSuccess(true);
          setTimeout(() => {
            navigate("/users");
          }, 500);
        } else {
          const tenantsResult = await dispatch.auth.fetchTenantsAsync();
          const tenantList = tenantsResult?.tenants || [];
          const targetPath = getInitialRoute(user, tenantList);
          setShowSuccess(true);
          setTimeout(() => {
            navigate(targetPath);
          }, 500);
        }
      }
      return;
    }

    const result = await dispatch.auth.loginAsync({ email, password });

    if (result?.success) {
      if (result.requiresMFA) {
        setTempToken(result.tempToken);
        setMfaStep(true);
        return;
      }
      const user = JSON.parse(getCookie("adminAuth") || "{}");
      const isSuperAdmin = normalizeRole(user.role) === "SUPERADMIN";

      if (isSuperAdmin) {
        setShowSuccess(true);
        setTimeout(() => {
          navigate("/users");
        }, 500);
      } else {
        const tenantsResult = await dispatch.auth.fetchTenantsAsync();
        const tenantList = tenantsResult?.tenants || [];
        const targetPath = getInitialRoute(user, tenantList);
        setShowSuccess(true);
        setTimeout(() => {
          navigate(targetPath);
        }, 500);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "10vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f4f6fa",
        p: { xs: 2, sm: 3, md: 4 },
        boxSizing: "border-box",
        fontFamily: '"Inter", sans-serif',
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1060,
          bgcolor: "#ffffff",
          borderRadius: { xs: "20px", md: "24px" },
          boxShadow: "0 24px 64px -12px rgba(15, 23, 42, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          overflow: "hidden",
          boxSizing: "border-box",
          p: { xs: 0, md: "7px 13px 7px 7px" },
          gap: { xs: 0, md: "8px" },
        }}
      >
        {/* Left Presentation / Showcase Section */}
        <Box
          sx={{
            flex: { xs: 1, md: "0 0 528px" },
            bgcolor: branding.secondaryColor || "secondary.main" || "#111326",
            background: branding.secondaryColor ? undefined : "linear-gradient(155deg, #101224 0%, #171a38 50%, #0f1124 100%)",
            borderRadius: { xs: "20px 20px 0 0", md: "16px" },
            pt: { xs: 4, sm: 5, md: "72px" },
            px: { xs: 3, sm: 4, md: "31.5px" },
            pb: 0,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            position: "relative",
            overflow: "hidden",
            boxSizing: "border-box",
            minHeight: { md: "620px" },
          }}
        >
          {/* Headline & Subtitle */}
          <Box sx={{ zIndex: 1, maxWidth: 465 }}>
            <Typography
              sx={{
                color: "#ffffff",
                fontSize: "24px",
                fontWeight: 700,
                lineHeight: "32px",
                letterSpacing: "-0.02em",
                fontFamily: '"Inter", sans-serif',
                mb: 1.5,
              }}
            >
              Unified hospitality operations,
              <br />
              refined for excellence.
            </Typography>
            <Typography
              sx={{
                color: "#8e99ac",
                fontSize: "13px",
                fontWeight: 400,
                lineHeight: 1.6,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              Manage check-ins, housekeeping, revenue, and audits in real time.
            </Typography>
          </Box>

          {/* Dashboard Mockup Image — grows to fill remaining space and pins to bottom */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              mt: "auto",
              pt: 4,
              mx: "-38px",
              mb: 0,
              zIndex: 1,
            }}
          >
            <Box
              component="img"
              src={loginShowcaseImg}
              alt={brand.showcaseAlt}
              sx={{
                width: "100%",
                maxWidth: 541,
                height: "auto",
                objectFit: "contain",
                display: "block",
                verticalAlign: "bottom",
              }}
            />
          </Box>
        </Box>

        {/* Right Form Section */}
        <Box
          sx={{
            flex: 1,
            bgcolor: "#ffffff",
            p: { xs: 3, sm: 4, md: "48px 44px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 340 }}>
            {/* Dynamic Brand Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2.5 }}>
              <Box
                component="img"
                src={
                  branding.logoUrl ||
                  branding.logoBlue ||
                  branding.logo ||
                  (typeof process !== 'undefined' && (process.env.REACT_APP_APP_LOGO || process.env.VITE_APP_LOGO)) ||
                  ((branding.appName || brand.brandName || '').toUpperCase().includes('PMS')
                    ? pmsGoLogo
                    : stekayLogoBlue)
                }
                alt={branding.logoAlt || brand.logoAlt}
                sx={{
                  width: 32,
                  height: 32,
                  objectFit: "contain",
                  display: "block",
                }}
              />
              <Typography
                sx={{
                  color: branding.primaryColor || "#3557e8",
                  fontSize: "24px",
                  fontWeight: 800,
                  fontFamily: '"Inter", sans-serif',
                  letterSpacing: "-0.025em",
                  lineHeight: 1,
                }}
              >
                {branding.appName || brand.brandName}
              </Typography>
            </Box>

            {/* Welcome Back & Subtitle */}
            <Box sx={{ mb: 2 }}>
              <Typography
                component="h1"
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  lineHeight: "32px",
                  color: "#111827",
                  fontFamily: '"Inter", sans-serif',
                  letterSpacing: "-0.02em",
                  mb: 0.75,
                }}
              >
                Welcome back
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 400,
                  lineHeight: "20px",
                  letterSpacing: "normal",
                  color: "#6b7280",
                  fontFamily: '"Inter", sans-serif',
                }}
              >
                Access your property management and audit tools.
              </Typography>
            </Box>

            {/* Account Locked Alert */}
            {accountLocked && (
              <Alert 
                severity="error" 
                icon={<LockOutlinedIcon fontSize="small" />}
                sx={{ mb: 2.5, borderRadius: "8px", fontSize: "13px" }}
              >
                Your account has been locked due to multiple failed login attempts. Please contact your administrator.
              </Alert>
            )}

            {/* Session Terminated Alert */}
            {terminatedMsg && !accountLocked && (
              <Alert severity="warning" sx={{ mb: 2.5, borderRadius: "8px", fontSize: "13px" }}>
                {terminatedMsg}
              </Alert>
            )}

            {/* General Error */}
            {error && !accountLocked && !terminatedMsg && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: "8px", fontSize: "13px" }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleLogin} autoComplete="off">
            {!mfaStep ? (
              <>
                {/* Email Field */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    component="label"
                    htmlFor="login-email"
                    sx={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#374151",
                      mb: 0.8,
                      fontFamily: '"Inter", sans-serif',
                    }}
                  >
                    Email
                  </Typography>
                  <TextField
                    fullWidth
                    id="login-email"
                    placeholder="Enter email"
                    variant="outlined"
                    value={email}
                    onChange={handleEmailChange}
                    autoComplete="new-password"
                    disabled={accountLocked}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailOutlineIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: "#ffffff",
                        fontSize: "13.5px",
                        fontFamily: '"Inter", sans-serif',
                        "& fieldset": {
                          borderColor: "#e5e7eb",
                        },
                        "&:hover fieldset": {
                          borderColor: "#cbd5e1",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: branding.primaryColor || "#3557e8",
                          borderWidth: "1.5px",
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        py: "11px",
                        "&::placeholder": {
                          color: "#9ca3af",
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>

                {/* Password Field */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0.8,
                    }}
                  >
                    <Typography
                      component="label"
                      htmlFor="login-password"
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#374151",
                        fontFamily: '"Inter", sans-serif',
                      }}
                    >
                      Password
                    </Typography>
                    <Link
                      to="/forgot-password"
                      style={{
                        color: branding.primaryColor || "#3557e8",
                        fontSize: "12.5px",
                        fontWeight: 600,
                        textDecoration: "none",
                        fontFamily: '"Inter", sans-serif',
                      }}
                    >
                      Forgot Password?
                    </Link>
                  </Box>
                  <TextField
                    fullWidth
                    id="login-password"
                    placeholder="Enter password"
                    variant="outlined"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    autoComplete="new-password"
                    disabled={accountLocked}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword((show) => !show)}
                            edge="end"
                            size="small"
                            sx={{ color: "#9ca3af" }}
                          >
                            {showPassword ? (
                              <Visibility sx={{ fontSize: 18 }} />
                            ) : (
                              <VisibilityOff sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: "#ffffff",
                        fontSize: "13.5px",
                        fontFamily: '"Inter", sans-serif',
                        "& fieldset": {
                          borderColor: "#e5e7eb",
                        },
                        "&:hover fieldset": {
                          borderColor: "#cbd5e1",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: branding.primaryColor || "#3557e8",
                          borderWidth: "1.5px",
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        py: "11px",
                        "&::placeholder": {
                          color: "#9ca3af",
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
              </>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Alert severity="info" sx={{ mb: 2, borderRadius: "8px", fontSize: "13px" }}>
                  MFA required for high-privilege account. Please enter the 6-digit code from your authenticator app.
                </Alert>
                <Typography
                  component="label"
                  htmlFor="mfa-verification-code"
                  sx={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#374151",
                    mb: 0.8,
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  MFA Verification Code
                </Typography>
                <TextField
                  fullWidth
                  id="mfa-verification-code"
                  placeholder="------"
                  variant="outlined"
                  required
                  value={mfaCode}
                  onChange={handleMfaCodeChange}
                  inputProps={{
                    maxLength: 6,
                    style: { textAlign: "center", letterSpacing: "8px", fontSize: "1.3rem", fontWeight: 700 },
                  }}
                  autoFocus
                  sx={{
                    mb: 1.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      bgcolor: "#ffffff",
                      "& fieldset": { borderColor: "#e5e7eb" },
                      "&.Mui-focused fieldset": { borderColor: branding.primaryColor || "#3557e8" },
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberDevice}
                      onChange={(e) => setRememberDevice(e.target.checked)}
                      sx={{
                        color: "#9ca3af",
                        "&.Mui-checked": { color: branding.primaryColor || "#3557e8" },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "13px", color: "#4b5563" }}>
                      Remember this device
                    </Typography>
                  }
                  sx={{ mt: 0.5, mb: 1, display: "flex", textAlign: "left" }}
                />
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => {
                    setMfaStep(false);
                    setMfaCode("");
                    setTempToken("");
                    if (error) dispatch.auth.setError(null);
                  }}
                  sx={{
                    mt: 0.5,
                    mb: 1.5,
                    color: "#6b7280",
                    textTransform: "none",
                    fontSize: "13px",
                    fontWeight: 600,
                    "&:hover": { color: "#111827", bgcolor: "transparent" },
                  }}
                >
                  ← Back to Login
                </Button>
              </Box>
            )}

            {/* Submit Button */}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={isLoading || accountLocked || (mfaStep && mfaCode.length < 6)}
              sx={{
                py: "11px",
                borderRadius: "8px",
                bgcolor: branding.primaryColor || "#4B6BFB",
                color: "#ffffff",
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "none",
                fontFamily: '"Inter", sans-serif',
                boxShadow: `0 6px 18px rgba(${branding.primaryColorRgb || '75, 107, 251'}, 0.35)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: branding.secondaryColor || "#3A56E8",
                  boxShadow: `0 8px 22px rgba(${branding.primaryColorRgb || '75, 107, 251'}, 0.45)`,
                },
                "&.Mui-disabled": {
                  bgcolor: "#94a3b8",
                  color: "#ffffff",
                  opacity: 0.6,
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : mfaStep ? (
                "Verify & Sign In"
              ) : (
                <>
                  Sign in to Dashboard
                  <ArrowForwardIcon sx={{ fontSize: 17 }} />
                </>
              )}
            </Button>
          </form>
        </Box>
      </Box>
    </Box>
    <Snackbar
      open={showSuccess}
      autoHideDuration={3000}
      onClose={() => setShowSuccess(false)}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Alert
        onClose={() => setShowSuccess(false)}
        severity="success"
        sx={{ width: "100%", boxShadow: 3, borderRadius: "10px" }}
      >
        Logged in successfully!
      </Alert>
    </Snackbar>
  </Box>
);
}
